import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { TutorialWizardComponent } from '../../../../ui/tutorial-wizard/tutorial-wizard/tutorial-wizard.component';
import { TutorialWizardSlideDirective } from '../../../../ui/tutorial-wizard/tutorial-wizard-slide.directive';
import { TutorialSlideContentComponent } from '../../../../ui/tutorial-wizard/tutorial-slide-content/tutorial-slide-content.component';
import { TutorialVersusContentComponent } from '../../../../ui/tutorial-wizard/tutorial-versus-content/tutorial-versus-content.component';
import { ThemePipe } from 'src/app/core/pipes/theme.pipe';
import { NgStyle } from '@angular/common';
import { GameTutorial } from '../game-tutorial';
import { imageAssetPath } from 'src/app/core/services/theme/theme-asset-path';

@Component({
  selector: 'app-blackjack-tutorial',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TutorialWizardComponent,
    TutorialWizardSlideDirective,
    TutorialSlideContentComponent,
    TutorialVersusContentComponent,
    NgStyle,
    ThemePipe,
  ],
  template: `
    <app-tutorial-wizard [totalPages]="3" [slideTitles]="slideTitles">
      <div appTutorialSlide>
        <app-tutorial-slide-content
          [imageUrl]="img('blackjack-tutorial-1.png')"
          [description]="t('blackjack-slide-1')"
        >
          <span overlay class="label--below-center" [ngStyle]="contentStyles | theme">{{
            t('blackjack-hit-or-stand')
          }}</span>
        </app-tutorial-slide-content>
      </div>

      <div appTutorialSlide>
        <div class="dual-image-slide">
          <div class="dual-image-container">
            @if (img('blackjack-tutorial-2.png'); as streakImage) {
              <img [src]="streakImage" alt="Blackjack streak" class="streak-image" />
            }
            <div class="dual-image-labels">
              <div class="dual-image-item">
                <span [ngStyle]="contentStyles | theme">{{ t('blackjack-streak-0') }}</span>
              </div>
              <div class="dual-image-item">
                <span [ngStyle]="contentStyles | theme">{{ t('blackjack-streak-2') }}</span>
              </div>
            </div>
          </div>
          <p class="slide-description" [ngStyle]="descriptionStyles | theme">
            {{ t('blackjack-slide-2') }}
          </p>
        </div>
      </div>

      <div appTutorialSlide>
        <app-tutorial-versus-content
          [imageUrl]="img('blackjack-tutorial-3.png')"
          [description]="t('blackjack-slide-3')"
        />
      </div>
    </app-tutorial-wizard>
  `,
  styles: [
    `
      .label--below-center {
        position: absolute;
        bottom: var(--spacing-x-large);
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
      }
      .dual-image-slide {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        gap: var(--spacing-medium);
        padding: var(--spacing-medium) 0;
      }
      .dual-image-container {
        position: relative;
        display: flex;
        justify-content: center;
        padding-top: var(--spacing-x-large);
      }
      .streak-image {
        max-width: 100%;
        max-height: 35vh;
        object-fit: contain;
        display: block;
      }
      .dual-image-labels {
        position: absolute;
        bottom: 10%;
        left: 0;
        right: 0;
        display: flex;
        padding: 0 var(--spacing-x-large);
      }
      .dual-image-item {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .slide-description {
        text-align: center;
        margin: 0;
        padding: 0 var(--spacing-small);
      }
    `,
  ],
})
export class BlackjackTutorialComponent implements GameTutorial {
  private translocoService = inject(TranslocoService);
  private themeService = inject(ThemeService);

  readonly slideTitles = [this.t('how-to-play'), this.t('how-to-win'), this.t('real-opponents')];

  readonly contentStyles: [string, string][] = [
    ['type', 'drawer-tutorial-graphic-text'],
    ['text-color', 'drawer-tutorial-graphic-text'],
  ];

  readonly descriptionStyles: [string, string][] = [
    ['type', 'drawer-tutorial-body'],
    ['text-color', 'drawer-tutorial-body'],
  ];

  t(key: string): string {
    return this.translocoService.translate(`components.tutorial-wizard.${key}`);
  }

  img(name: string): string | null {
    return this.themeService.assetUrl(imageAssetPath(name));
  }
}
